import React, { useEffect, useState } from "react";
import { Button, Input, Space, message } from "antd";
import { CheckOutlined, EditOutlined } from "@ant-design/icons";
import { patchRemnants } from "api/ProductRemnants";
import {
  recomputeRemnantsManage,
  setShareFormulaRemnantsManage,
  setComboShareFormulaRemnantsManage,
} from "api/RemnantsManage";

export type FormulaField =
  | "share_in_wildberries"
  | "share_in_ozon"
  | "share_in_combo";

type Props = {
  article: string;
  value: string | null;
  onSaved: (next: string | null) => void;
  /** Called after formula save + local waterfall so parent can reload rows. */
  onRecalculated?: () => void | Promise<void>;
  /** Legacy article-level share (N=1). Required unless mpWarehouseId/warehouseId is set. */
  field?: FormulaField;
  /** Phase 6: per mp_warehouse formula (WB/Ozon). */
  mpWarehouseId?: number;
  /** Фаза D: per self_warehouse combo share override. */
  warehouseId?: number;
};

const FormulaCell: React.FC<Props> = ({
  article,
  field,
  mpWarehouseId,
  warehouseId,
  value,
  onSaved,
  onRecalculated,
}) => {
  const [editing, setEditing] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(value ?? "");
  }, [value, editing]);

  const save = async () => {
    const next = draft.trim() === "" ? null : draft.trim();
    if (next === (value || null)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      if (mpWarehouseId != null) {
        await setShareFormulaRemnantsManage({
          article,
          mp_warehouse_id: mpWarehouseId,
          share_formula: next,
        });
      } else if (warehouseId != null) {
        await setComboShareFormulaRemnantsManage({
          article,
          warehouse_id: warehouseId,
          share_formula: next,
        });
      } else if (field) {
        await patchRemnants([{ article, data: { [field]: next } }]);
        await recomputeRemnantsManage({ articles: [article] });
      } else {
        throw new Error("formula cell: missing field, mpWarehouseId or warehouseId");
      }
      onSaved(next);
      setEditing(false);
      message.success("Формула сохранена, остатки пересчитаны");
      await onRecalculated?.();
    } catch {
      message.error("Не удалось сохранить формулу");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <Space.Compact style={{ width: "100%" }}>
        <Input
          size="small"
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onPressEnter={save}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setDraft(value ?? "");
              setEditing(false);
            }
          }}
          disabled={saving}
        />
        <Button
          size="small"
          type="primary"
          icon={<CheckOutlined />}
          loading={saving}
          onClick={save}
          title="Принять"
        />
      </Space.Compact>
    );
  }

  return (
    <span
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => setEditing(true)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        cursor: "pointer",
        maxWidth: "100%",
        minHeight: 22,
      }}
    >
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value || "—"}
      </span>
      {hovered ? (
        <EditOutlined
          style={{ color: "#1677ff", flexShrink: 0 }}
          onClick={(e) => {
            e.stopPropagation();
            setEditing(true);
          }}
        />
      ) : null}
    </span>
  );
};

export default FormulaCell;
